// components/HouseSidebar.jsx

'use client'

import React, { useState, useCallback } from 'react'
import Link from 'next/link'
import {
    FaBriefcase,
    FaHome,
    FaKey,
} from 'react-icons/fa'
import { useMobileMenu } from '@/contexts/MobileMenuContext'
import { useAuth } from '@/contexts/AuthContext'
import { usePathname } from 'next/navigation'
import { houseNavItemsFor } from '@/utils/houseNav'
import { isAccountType, isOrgAccountType } from '@/utils/builtInApplets'

// ─── Icon base URL ────────────────────────────────────────────────────────────
const ICON_BASE_URL = 'https://fireops.blob.core.windows.net/icons'

// ─── House-only top-level items (above the divider, alongside House Admin) ─────
// These use un-prefixed routes (not /house/*) to match the house-only account
// pages: app/housesubscriptions/ and app/helpcenterhouse/.
const HOUSE_TOP_ITEMS = [
    { href: '/housesubscriptions', label: 'Subscriptions', iconPath: 'global/housedues.png' },
    { href: '/helpcenterhouse',    label: 'Help Center',   iconPath: 'global/houseinfo.png' },
]

// ─── House nav items ──────────────────────────────────────────────────────────
// Shared with the main Sidebar and the house dashboard — see utils/houseNav.js.

// ─── Blob icon with inline fallback ──────────────────────────────────────────
function HouseNavIcon({ iconPath }) {
    const [error, setError] = useState(false)

    if (error || !iconPath) {
        return (
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
                <span className="w-3 h-3 sm:w-4 sm:h-4 block rounded-sm bg-gray-400" />
            </div>
        )
    }

    return (
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
            <img
                src={`${ICON_BASE_URL}/${iconPath}`}
                alt=""
                className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                onError={() => setError(true)}
            />
        </div>
    )
}

// ─── Sidebar component ────────────────────────────────────────────────────────
export default function HouseSidebar() {
    const { isMobileOpen, closeMobileMenu } = useMobileMenu()
    const { currentOrg, isHouseAdmin } = useAuth()

    // Legacy HouseOnlyMenu.razor has TWO branches and we were rendering only the
    // second one, to everybody:
    //
    //   OrgAccountType != HouseOnly  (a shared station login inside a normal
    //       department org)  ->  House Admin + the house pages. Nothing else.
    //   OrgAccountType == HouseOnly  ->  additionally My Account, Subscriptions
    //       and Help Center — and legacy points those at HOUSE-SPECIFIC pages
    //       (/myaccounthouse, /subscriptionshouse), not the org ones.
    //
    // Showing them to a shared login is what produced the two live errors on
    // 2026-08-08: Subscriptions returned "user not authorized" (correctly — that
    // account has no rights over the department org's subscriptions) and My
    // Account threw a null reference, because it is the ORG page and expects
    // data a station login does not have.
    const isHouseOnlyOrg = isOrgAccountType(currentOrg, 'HouseOnly')

    // A shared station login is not a person: no name, no email, no 2FA — and
    // plausibly no user record at all, which is why `getmyaccount` answered with
    // a .NET null-reference for one (owner report 2026-08-08). There is nothing
    // for such an account to manage, so it gets no My Account. A HouseOnly
    // account IS a real person and can use the ordinary page — verified against
    // Alicia House on 2026-08-09.
    //
    // Gated on the ACCOUNT, not just the org: a shared login inside a HouseOnly
    // org would otherwise slip through and hit the same crash.
    const isSharedLogin = isAccountType(currentOrg, 'SharedHouse')
    const canManageAccount = isHouseOnlyOrg && !isSharedLogin
    const pathname = usePathname()

    const handleNavClick = useCallback(() => {
        closeMobileMenu()
    }, [closeMobileMenu])

    return (
        <>
            {/* Mobile overlay */}
            {isMobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/50 z-30 backdrop-blur-sm"
                    style={{ top: 'var(--header-height)' }}
                    onClick={closeMobileMenu}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar panel */}
            <aside
                className={`
                    fixed left-0
                    w-[280px] sm:w-80 bg-gray-800 text-white
                    flex flex-col shadow-lg z-40
                    transition-transform duration-300 ease-in-out overflow-hidden
                    lg:translate-x-0
                    sidebar-panel
                    ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
                style={{
                    // Height lives in `.sidebar-panel`, NOT here: it needs two
                    // declarations (a vh fallback then dvh) and an inline style can
                    // hold only one value per property. Setting it here would also
                    // beat the class and silently restore the bug — the panel sized
                    // to the LARGE viewport, so its bottom sat under the phone's
                    // browser bar where scrolling could not reach it.
                    top: 'var(--header-height)',
                }}
                role="navigation"
                aria-label="House admin navigation"
            >
                <nav className="flex-1 overflow-y-auto custom-scrollbar sidebar-scroll">
                    <ul className="py-1 sm:py-2">

                        {/* Home */}
                        <li>
                            <Link
                                href="/househome"
                                onClick={handleNavClick}
                                className={`
                                    flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                    transition-colors text-sm touch-target
                                    ${pathname === '/househome'
                                    ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white border-l-4 border-transparent'
                                }
                                `}
                            >
                                <FaHome className="text-base sm:text-lg flex-shrink-0" />
                                <span className="truncate">Home</span>
                            </Link>
                        </li>

                        {/* My Account — HouseOnly people only, never shared logins.
                            Points at the ordinary /myaccount: legacy's separate
                            /myaccounthouse was a layout variant, not different
                            functionality, and the ported page already calls all
                            seven of the same endpoints. */}
                        {canManageAccount && (
                        <li>
                            <Link
                                href="/myaccount"
                                onClick={handleNavClick}
                                className={`
                                    flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                    transition-colors text-sm touch-target
                                    ${pathname === '/myaccount'
                                    ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white border-l-4 border-transparent'
                                }
                                `}
                            >
                                <FaKey className="text-base sm:text-lg flex-shrink-0" />
                                <span className="truncate">My Account</span>
                            </Link>
                        </li>
                        )}

                        {/* House Admin — admins of THIS house only. The page and
                            every write endpoint re-check server-side; this just
                            stops offering a link that ends in a refusal. */}
                        {isHouseAdmin && (
                        <li>
                            <Link
                                href="/houseadmin"
                                onClick={handleNavClick}
                                className={`
                                    flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                    transition-colors text-sm touch-target font-semibold
                                    ${pathname === '/houseadmin'
                                    ? 'bg-[#b5a472] text-white border-l-4 border-yellow-500'
                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white border-l-4 border-transparent'
                                }
                                `}
                            >
                                <FaBriefcase className="text-base sm:text-lg flex-shrink-0" />
                                <span className="truncate">House Admin</span>
                            </Link>
                        </li>
                        )}

                        {/* Subscriptions + Help Center — HouseOnly orgs only, per
                            legacy. A shared login in a department org has no rights
                            over that org's subscriptions. */}
                        {isHouseOnlyOrg && HOUSE_TOP_ITEMS.map((item) => {
                            const isActive = pathname === item.href
                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        onClick={handleNavClick}
                                        className={`
                                            flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                            transition-colors text-sm touch-target
                                            ${isActive
                                            ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white border-l-4 border-transparent'
                                        }
                                        `}
                                    >
                                        <HouseNavIcon iconPath={item.iconPath} />
                                        <span className="truncate">{item.label}</span>
                                    </Link>
                                </li>
                            )
                        })}

                        {/* Orange divider */}
                        <li className="px-3 sm:px-4 py-1">
                            <div className="h-[3px] rounded-full bg-orange-500" />
                        </li>

                        {/* Nav items */}
                        {houseNavItemsFor(currentOrg?.orgId).map((item) => {
                            const isActive = pathname === item.href
                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        onClick={handleNavClick}
                                        className={`
                                            flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5
                                            transition-colors text-sm touch-target
                                            ${isActive
                                            ? 'bg-gray-700 text-white border-l-4 border-yellow-500'
                                            : 'text-gray-300 hover:bg-gray-700 hover:text-white border-l-4 border-transparent'
                                        }
                                        `}
                                    >
                                        <HouseNavIcon iconPath={item.iconPath} />
                                        <span className="truncate">{item.label}</span>
                                    </Link>
                                </li>
                            )
                        })}

                    </ul>
                </nav>
            </aside>
        </>
    )
}